import React, { useState, useMemo } from 'react';
import { X, Settings, AlertCircle, CheckCircle } from 'lucide-react';

const DRIVER_TYPE_OPTIONS = [
  '$ / ARN',
  '$ / ARN LM',
  '$ / ARN LY',
  '$ / ORN',
  '$ / ORN LM',
  '$ / ORN LY',
  '$ LM',
  '$POR Projection',
  'Budget',
  'LY Same Month',
  '% of Revenue',
  '% of Selected Revenue',
  'REVPAR Projection',
  'Static Entry'
];

/**
 * BulkDriverUpdateModal - Modal for updating forecast drivers on multiple accounts
 *
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Callback to close the modal
 * @param {Array} selectedAccounts - Array of selected account objects
 * @param {function} onUpdate - Callback when update is confirmed (driver, accounts)
 */
const BulkDriverUpdateModal = ({ isOpen, onClose, selectedAccounts = [], onUpdate }) => {
  const [selectedDriver, setSelectedDriver] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // Group accounts by type for preview
  const accountsByType = useMemo(() => {
    const groups = {};
    selectedAccounts.forEach(account => {
      const type = account.accountType || 'Unknown';
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(account);
    });
    return groups;
  }, [selectedAccounts]);

  // Handle update confirmation
  const handleUpdate = async () => {
    if (!selectedDriver) return;

    setIsUpdating(true);
    try {
      await onUpdate(selectedDriver, selectedAccounts);
      setUpdateSuccess(true);

      // Close modal after short delay to show success
      setTimeout(() => {
        setUpdateSuccess(false);
        setSelectedDriver('');
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error updating drivers:', error);
      alert('Failed to update drivers. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Return null after all hooks are called
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full pointer-events-auto max-h-[80vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <Settings className="text-orange-600" size={24} />
              <h2 className="text-xl font-bold text-gray-900">Bulk Update Forecast Driver</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isUpdating}
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {/* Success Message */}
            {updateSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <CheckCircle className="text-green-600" size={20} />
                <span className="text-sm font-medium text-green-800">
                  Successfully updated {selectedAccounts.length} account(s)!
                </span>
              </div>
            )}

            {/* Selected Accounts Summary */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                Selected Accounts ({selectedAccounts.length})
              </h3>
              <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                {Object.entries(accountsByType).map(([type, accounts]) => (
                  <div key={type} className="mb-3 last:mb-0">
                    <div className="text-xs font-semibold text-gray-600 mb-1">
                      {type} ({accounts.length})
                    </div>
                    <div className="space-y-1">
                      {accounts.map(account => (
                        <div
                          key={account.id}
                          className="text-xs text-gray-700 flex items-center gap-2 py-1"
                        >
                          <span className="font-mono text-gray-500">{account.accountNumber}</span>
                          <span className="flex-1">{account.accountName}</span>
                          {account.driverType && (
                            <span className="text-[10px] px-2 py-0.5 bg-gray-200 rounded">
                              {account.driverType}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Driver Selection */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                New Forecast Driver
              </label>
              <select
                value={selectedDriver}
                onChange={(e) => setSelectedDriver(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                disabled={isUpdating}
              >
                <option value="">Select a driver...</option>
                {DRIVER_TYPE_OPTIONS.map(driver => (
                  <option key={driver} value={driver}>
                    {driver}
                  </option>
                ))}
              </select>
            </div>

            {/* Warning */}
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={16} />
              <div className="text-xs text-yellow-800">
                <p className="font-semibold mb-1">Important Notes:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>This will replace the current driver for all selected accounts</li>
                  <li>Existing forecast data will be cleared when the driver changes</li>
                  <li>You will need to re-enter inputs for the new driver</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isUpdating}
            >
              Cancel
            </button>
            <button
              onClick={handleUpdate}
              disabled={!selectedDriver || isUpdating}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isUpdating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Updating...
                </>
              ) : (
                <>
                  Update {selectedAccounts.length} Account{selectedAccounts.length !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BulkDriverUpdateModal;
